import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, CalendarRange, Search, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Card } from '@/shared/components/ui/Card';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useUser } from '@/shared/hooks/useAuth';
import { useLeaves, useLeaveMutations } from '../hooks/useHr';
import { SubmitLeaveModal } from '../components/SubmitLeaveModal';
import { LeaveDetailDrawer } from '../components/LeaveDetailDrawer';
import {
  DEPT_LABELS,
  LEAVE_STATUS_LABEL,
  LEAVE_STATUS_TONE,
  LEAVE_TYPE_LABELS,
  LEAVE_TYPE_OPTIONS,
  fmtDate,
} from '../constants';
import type { Leave, LeaveStatus, SubmitLeavePayload } from '../types';

function ApprovalTrail({ leave }: { leave: Leave }) {
  const step1done = ['admin_approved', 'approved', 'rejected'].includes(leave.status);
  const step1ok = leave.status === 'admin_approved' || leave.status === 'approved';
  const step2done = leave.status === 'approved' || (leave.status === 'rejected' && !!leave.sa_reviewer_name);
  const step2ok = leave.status === 'approved';

  const Dot = ({ done, ok }: { done: boolean; ok: boolean }) => (
    <span
      className={`inline-block h-2 w-2 rounded-full ${
        done ? (ok ? 'bg-success' : 'bg-danger') : 'bg-warning'
      }`}
    />
  );

  return (
    <div className="flex flex-col gap-1 text-[10px] text-on-surface-variant">
      <div className="flex items-center gap-1.5">
        <Dot done={step1done} ok={step1ok} />
        <span>Dept Admin{leave.admin_reviewer_name ? `: ${leave.admin_reviewer_name}` : ''}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Dot done={step2done} ok={step2ok} />
        <span>Executive{leave.sa_reviewer_name ? `: ${leave.sa_reviewer_name}` : ''}</span>
      </div>
    </div>
  );
}

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'admin_approved', label: 'Awaiting Executive' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const TYPE_FILTER_OPTIONS = [{ value: 'all', label: 'All Types' }, ...LEAVE_TYPE_OPTIONS];

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All Requests' },
  { value: 'mine', label: 'My Requests' },
  { value: 'team', label: 'Team Requests' },
];

export function LeavesPage() {
  const user = useUser();
  const isAdmin = user?.role === 'admin';
  const isSA = user?.role === 'superadmin';
  const isPriv = isAdmin || isSA;

  const { data: leaves = [], isLoading, isError, isFetching, refetch } = useLeaves();
  const { submitLeave, reviewLeave } = useLeaveMutations();

  const [submitOpen, setSubmitOpen] = useState(false);
  const [selected, setSelected] = useState<Leave | null>(null);

  // Toolbar filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [scope, setScope] = useState<'all' | 'mine' | 'team'>('all');

  const handleSubmit = (payload: SubmitLeavePayload) => {
    submitLeave.mutate(payload, {
      onSuccess: () => {
        toast.success('Leave request submitted.');
        setSubmitOpen(false);
      },
      onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to submit leave.'),
    });
  };

  const handleReview = (leave: Leave, status: 'approved' | 'rejected') => {
    reviewLeave.mutate(
      { id: leave.id, status },
      {
        onSuccess: () => {
          const msg =
            status === 'approved'
              ? isSA
                ? 'Leave fully approved.'
                : 'Approved — now awaiting executive approval.'
              : 'Leave rejected.';
          toast.success(msg);
          setSelected(null);
        },
        onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to update leave.'),
      },
    );
  };

  // KPI counts over the full (role-scoped) dataset
  const kpis = useMemo(() => {
    let pending = 0;
    let awaiting = 0;
    let approved = 0;
    let rejected = 0;
    for (const l of leaves) {
      if (l.status === 'pending') pending++;
      else if (l.status === 'admin_approved') awaiting++;
      else if (l.status === 'approved') approved++;
      else if (l.status === 'rejected') rejected++;
    }
    return { pending, awaiting, approved, rejected };
  }, [leaves]);

  // Client-side filtering over real backend data (no fabrication)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leaves.filter((l) => {
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (typeFilter !== 'all' && l.type !== typeFilter) return false;
      if (isPriv && scope === 'mine' && l.user_id !== user?.id) return false;
      if (isPriv && scope === 'team' && l.user_id === user?.id) return false;
      if (q) {
        const hay = [
          l.employee_name,
          l.reason,
          LEAVE_TYPE_LABELS[l.type] ?? l.type,
          DEPT_LABELS[l.employee_dept ?? ''] ?? l.employee_dept,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [leaves, search, statusFilter, typeFilter, scope, isPriv, user?.id]);

  const canReview = (l: Leave) =>
    (isAdmin && l.status === 'pending') || (isSA && l.status === 'admin_approved');

  const renderActions = (l: Leave) => {
    if (isAdmin && l.status === 'pending') {
      return (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleReview(l, 'approved'); }}>
            Approve
          </Button>
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleReview(l, 'rejected'); }}>
            Reject
          </Button>
        </div>
      );
    }
    if (isSA && l.status === 'admin_approved') {
      return (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleReview(l, 'approved'); }}>
            Final Approve
          </Button>
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleReview(l, 'rejected'); }}>
            Reject
          </Button>
        </div>
      );
    }
    if (isSA && l.status === 'pending') {
      return <span className="text-[10px] italic text-on-surface-variant/60">Awaiting dept admin</span>;
    }
    return <span className="text-[11px] text-on-surface-variant/40">—</span>;
  };

  const columns: Column<Leave>[] = [
    ...(isPriv
      ? [
          {
            key: 'employee',
            header: 'Employee',
            render: (l: Leave) => (
              <div className="min-w-0">
                <div className="font-medium text-on-surface">{l.employee_name ?? '—'}</div>
                <div className="text-[10px] text-on-surface-variant">
                  {DEPT_LABELS[l.employee_dept ?? ''] ?? l.employee_dept}
                </div>
              </div>
            ),
          } as Column<Leave>,
        ]
      : []),
    { key: 'type', header: 'Type', render: (l) => <span>{LEAVE_TYPE_LABELS[l.type] ?? l.type}</span> },
    { key: 'from_date', header: 'From', render: (l) => fmtDate(l.from_date) },
    { key: 'to_date', header: 'To', render: (l) => fmtDate(l.to_date) },
    { key: 'days', header: 'Days', align: 'center', render: (l) => <span className="font-semibold">{l.days}</span> },
    {
      key: 'reason',
      header: 'Reason',
      render: (l) => (
        <span className="block max-w-[160px] truncate text-on-surface-variant" title={l.reason || ''}>
          {l.reason || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (l) => <Badge tone={LEAVE_STATUS_TONE[l.status] ?? 'neutral'}>{LEAVE_STATUS_LABEL[l.status] ?? l.status}</Badge>,
    },
    {
      key: 'trail',
      header: isPriv ? 'Approval' : 'Submitted',
      render: (l) => (isPriv ? <ApprovalTrail leave={l} /> : <span className="text-[11px] text-on-surface-variant">{fmtDate(l.created_at)}</span>),
    },
    ...(isPriv ? [{ key: 'actions', header: 'Actions', align: 'right', render: renderActions } as Column<Leave>] : []),
  ];

  const showKpis = leaves.length > 0;

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarRange className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Leave Management</h1>
            <p className="text-body-sm text-on-surface-variant">
              {filtered.length} of {leaves.length} request{leaves.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <Button onClick={() => setSubmitOpen(true)} size="md">
          <Plus className="h-4 w-4" /> Submit Leave
        </Button>
      </div>

      {/* KPI dashboard — counts derived from real backend statuses */}
      {showKpis && (
        <div className={`grid gap-3 ${isPriv ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'}`}>
          <Kpi label="Pending" value={kpis.pending} tone="warning" />
          {isPriv && <Kpi label="Awaiting Executive" value={kpis.awaiting} tone="info" />}
          <Kpi label="Approved" value={kpis.approved} tone="success" />
          <Kpi label="Rejected" value={kpis.rejected} tone="danger" />
        </div>
      )}

      {/* Enterprise toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[200px] flex-1">
          <Input
            name="leave-search"
            placeholder={isPriv ? 'Search employee, reason, type…' : 'Search reason, type…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="w-[170px]">
          <Select
            name="leave-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LeaveStatus | 'all')}
            options={STATUS_FILTER_OPTIONS}
          />
        </div>
        <div className="w-[150px]">
          <Select
            name="leave-type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            options={TYPE_FILTER_OPTIONS}
          />
        </div>
        {isPriv && (
          <div className="w-[160px]">
            <Select
              name="leave-scope-filter"
              value={scope}
              onChange={(e) => setScope(e.target.value as 'all' | 'mine' | 'team')}
              options={SCOPE_OPTIONS}
            />
          </div>
        )}
        <Button variant="outline" size="md" onClick={() => refetch()} loading={isFetching} aria-label="Refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState title="Failed to load leaves" description="Check your connection or try refreshing." />
      ) : (
        <Table
          columns={columns}
          data={filtered}
          rowKey={(l) => l.id}
          onRowClick={(l) => setSelected(l)}
          empty={leaves.length === 0 ? 'No leave requests found.' : 'No requests match your filters.'}
        />
      )}

      <SubmitLeaveModal
        open={submitOpen}
        loading={submitLeave.isPending}
        onClose={() => setSubmitOpen(false)}
        onSubmit={handleSubmit}
      />

      <LeaveDetailDrawer
        leave={selected}
        open={!!selected}
        isPriv={isPriv}
        onClose={() => setSelected(null)}
        actions={
          selected && canReview(selected) ? (
            <>
              <Button
                variant="primary"
                size="sm"
                loading={reviewLeave.isPending}
                onClick={() => handleReview(selected, 'approved')}
              >
                {isSA ? 'Final Approve' : 'Approve'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                loading={reviewLeave.isPending}
                onClick={() => handleReview(selected, 'rejected')}
              >
                Reject
              </Button>
            </>
          ) : undefined
        }
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'warning' | 'info' | 'success' | 'danger';
}) {
  const toneCls =
    tone === 'success'
      ? 'text-success'
      : tone === 'info'
        ? 'text-info'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-warning';
  return (
    <Card className="px-4 py-3">
      <p className={`text-display-sm font-semibold ${toneCls}`}>{value}</p>
      <p className="mt-1 text-body-sm text-on-surface-variant">{label}</p>
    </Card>
  );
}
